import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthProvider';

const TokenValidationContext = createContext();

export const useTokenValidation = () => {
  const context = useContext(TokenValidationContext);
  if (!context) {
    throw new Error('useTokenValidation must be used within TokenValidationProvider');
  }
  return context;
};

/**
 * TokenValidationProvider manages the token validation state across the application
 * 
 * Features:
 * - Real-time validation status from backend
 * - Smart modal dismissal tracking (daily)
 * - Error handling with exponential backoff
 * - Context-aware re-appearance logic
 * 
 * Note: Frontend caching removed to prevent sync issues with backend
 */
export const TokenValidationProvider = ({ children }) => {
  const { user, token } = useAuth();
  
  // Check demo mode directly from localStorage
  const [isDemoMode, setIsDemoMode] = useState(() => {
    return JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
  });
  
  const [validationStatus, setValidationStatus] = useState(null);
  const [expiredAccounts, setExpiredAccounts] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Cache keys for localStorage (dismissal only - no validation caching)
  const DISMISSAL_CACHE_KEY = `modal_dismissal_${user?.[0]?.id}`;


  /**
   * Check if modal was dismissed today
   */
  const wasModalDismissedToday = useCallback(() => {
    try {
      const dismissed = localStorage.getItem(DISMISSAL_CACHE_KEY);
      if (dismissed) {
        const { timestamp } = JSON.parse(dismissed);
        const today = new Date().toDateString();
        const dismissedDate = new Date(timestamp).toDateString();
        return today === dismissedDate;
      }
    } catch (error) {
      console.warn('Failed to parse dismissal data:', error);
    }
    return false;
  }, [DISMISSAL_CACHE_KEY]);

  /**
   * Mark modal as dismissed for today
   */
  const markModalDismissed = useCallback(() => {
    try {
      localStorage.setItem(DISMISSAL_CACHE_KEY, JSON.stringify({
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Failed to cache dismissal data:', error);
    }
  }, [DISMISSAL_CACHE_KEY]);

  /**
   * Exponential backoff retry logic
   */
  const retryWithBackoff = useCallback(async (fn, maxRetries = 3) => {
    let retries = 0;
    let delay = 1000; // Start with 1 second

    while (retries < maxRetries) {
      try {
        return await fn();
      } catch (error) {
        retries++;
        if (retries >= maxRetries) {
          throw error;
        }
        
        // Wait with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay));
        delay = Math.min(delay * 2, 8000); // Cap at 8 seconds
      }
    }
  }, []);

  /**
   * Fetch token validation status from API (always fresh, no caching)
   */
  const fetchValidationStatus = useCallback(async (forceValidation = false) => {
    if (!user?.[0]?.id || !token) return;

    setIsLoading(true);
    setLastError(null);

    try {
      const endpoint = '/connect/token-validation/status/';
      const queryParam = forceValidation ? '?force=true' : '';
      
      const response = await retryWithBackoff(async () => {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}${queryParam}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: Failed to fetch validation status`);
        }

        return res.json();
      });

      // const status = response.validation_status;
      const status = response.token_status.is_valid;
      const expired = response.expired_account_details || [];

      setValidationStatus(status);
      setExpiredAccounts(expired);

      // Show modal if there are expired accounts and modal wasn't dismissed today
      if (expired.length > 0 && !wasModalDismissedToday()) {
        setIsModalVisible(true);
      }

      return { status, expired };

    } catch (error) {
      console.error('Token validation failed:', error);
      setLastError(error.message);
      
      // On error, clear the state rather than falling back to stale cache
      setValidationStatus(null);
      setExpiredAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, token, isDemoMode, retryWithBackoff, wasModalDismissedToday]);

  /**
   * Check validation status (always fetch fresh from backend)
   */
  const checkValidationStatus = useCallback(async (forceValidation = false) => {
    if (!user?.[0]?.id) return;

    // Always fetch fresh data from backend to ensure synchronization
    await fetchValidationStatus(forceValidation);
  }, [user, fetchValidationStatus]);

  /**
   * Check before user attempts to post
   */
  const checkBeforePosting = useCallback(async () => {
    await checkValidationStatus();
    
    // If there are expired accounts, show modal regardless of dismissal
    if (expiredAccounts.length > 0) {
      setIsModalVisible(true);
      return false; // Indicate posting should be blocked
    }
    
    return true; // Safe to post
  }, [checkValidationStatus, expiredAccounts]);

  /**
   * Dismiss modal and track dismissal
   */
  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
    markModalDismissed();
  }, [markModalDismissed]);

  /**
   * Force validation (bypass cache)
   */
  const forceValidation = useCallback(async () => {
    await fetchValidationStatus(true);
  }, [fetchValidationStatus]);

  /**
   * Clear cache (only clears modal dismissal, forces fresh validation)
   */
  const clearCache = useCallback(() => {
    localStorage.removeItem(DISMISSAL_CACHE_KEY);
    setValidationStatus(null);
    setExpiredAccounts([]);
    // Trigger fresh validation
    if (user?.[0]?.id) {
      checkValidationStatus(true);
    }
  }, [DISMISSAL_CACHE_KEY, user, checkValidationStatus]);

  // Listen for demo mode changes and OAuth operations
  useEffect(() => {
    const handleDemoModeChange = () => {
      const newDemoMode = JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
      setIsDemoMode(newDemoMode);
    };

    const handleOAuthComplete = () => {
      // Force fresh validation after OAuth operations
      if (user?.[0]?.id) {
        console.log('TokenValidationProvider: OAuth operation completed, refreshing validation status');
        checkValidationStatus(true);
      }
    };

    // Listen for custom events
    window.addEventListener('demoModeChanged', handleDemoModeChange);
    window.addEventListener('oauthComplete', handleOAuthComplete);
    window.addEventListener('tokenRefreshed', handleOAuthComplete);
    
    return () => {
      window.removeEventListener('demoModeChanged', handleDemoModeChange);
      window.removeEventListener('oauthComplete', handleOAuthComplete);
      window.removeEventListener('tokenRefreshed', handleOAuthComplete);
    };
  }, [user, checkValidationStatus]);

  // Initialize validation check on user change
  useEffect(() => {
    if (user?.[0]?.id) {
      checkValidationStatus();
    }
  }, [user, checkValidationStatus]);

  const value = {
    // State
    validationStatus,
    expiredAccounts,
    isModalVisible,
    isLoading,
    lastError,
    
    // Actions
    checkValidationStatus,
    checkBeforePosting,
    dismissModal,
    forceValidation,
    clearCache,
    
    // Utilities
    hasExpiredAccounts: expiredAccounts.length > 0,
    isValidated: validationStatus !== null,
  };

  return (
    <TokenValidationContext.Provider value={value}>
      {children}
    </TokenValidationContext.Provider>
  );
};

export default TokenValidationProvider; 